import React, { useRef, useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Upload, Loader2, X } from 'lucide-react';
import { uploadService } from '@/services/upload';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface ImageUrlInputProps {
  value: string;
  onChange: (_value: string) => void;
  placeholder?: string;
  className?: string;
  /** Optional: pass to show error state on input */
  error?: boolean;
}

/** Input + upload button for image URL. Use in forms and list items. */
export const ImageUrlInput: React.FC<ImageUrlInputProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  error,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (value) {
      setIsImageLoading(true);
    } else {
      setIsImageLoading(false);
    }
  }, [value]);

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Arquivo inválido',
        description: 'Por favor, selecione uma imagem.',
        variant: 'error',
      });
      return;
    }

    setIsUploading(true);
    try {
      const { url } = await uploadService.uploadImage(file);
      onChange(url);
      toast({
        title: 'Imagem enviada',
        description: 'A URL da imagem foi preenchida.',
      });
    } catch (err) {
      console.error('Erro ao fazer upload da imagem:', err);
      toast({
        title: 'Erro ao fazer upload',
        description: 'Não foi possível fazer upload da imagem. Tente novamente.',
        variant: 'error',
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleRemoveImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange('');
  };

  return (
    <div className={className}>
      <div className="flex gap-2">
        <Input
          type="url"
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`flex-1 ${error ? 'border-red-500 focus:border-red-500' : ''}`}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleUploadClick}
          disabled={isUploading}
          title="Enviar imagem"
          className="shrink-0"
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
        </Button>
      </div>
      <div
        role="button"
        tabIndex={0}
        className={cn(
          'mt-3 h-32 rounded-md border-2 border-dashed overflow-hidden flex items-center justify-center cursor-pointer transition-colors relative',
          isDragOver ? 'border-gray-600 bg-gray-100' : 'border-gray-400 bg-gray-50'
        )}
        onClick={handleUploadClick}
        onKeyDown={(e) => e.key === 'Enter' && handleUploadClick()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        aria-label="Arraste uma imagem ou clique para selecionar"
      >
        {value ? (
          <>
            {isImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" aria-hidden />
              </div>
            )}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={value}
              alt=""
              className={cn(
                'h-full w-full object-cover transition-opacity',
                isImageLoading ? 'opacity-0 absolute' : 'opacity-100'
              )}
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
            />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
              title="Remover imagem"
              aria-label="Remover imagem"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : isUploading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" aria-hidden />
          </div>
        ) : (
          <span className="flex items-center gap-2 text-xs text-gray-400">
            <Upload className="w-5 h-5 shrink-0 text-gray-400" />
            Arraste uma imagem ou clique para selecionar
          </span>
        )}
      </div>
    </div>
  );
};
